"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { ROUTE_CONTRACTS } from "@cabal/shared/api-client";
import type { TagsListResponse } from "@cabal/shared/domain/session/team/tag/types.js";
import { useApi } from "@/contexts/api";
import { useRouteAuthEnabled } from "@/hooks/routing";
import { useQueryHook } from "@/lib/query/use-query-hook";
import type { QueryResult } from "@/types/async-protocol";
import { tagsKeys } from "./keys";

type PublicTag = TagsListResponse["tags"][number];

interface TagsContextValue {
  tagsQuery: QueryResult<PublicTag[]>;
  tagsById: ReadonlyMap<string, PublicTag>;
  tagsByName: ReadonlyMap<string, PublicTag>;
}

const EMPTY_TAGS: PublicTag[] = [];

const TagsContext = createContext<TagsContextValue | null>(null);

export function TagsProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const routeEnabled = useRouteAuthEnabled({
    route: ROUTE_CONTRACTS.tags.list,
  });
  const tagsResponseQuery = useQueryHook<TagsListResponse>({
    queryKey: tagsKeys.all,
    queryFn: () => api.tags.list(),
    options: { enabled: routeEnabled },
  });
  const tags = tagsResponseQuery.data?.tags ?? EMPTY_TAGS;

  const tagsResponseQueryData = tagsResponseQuery.data;
  const tagsResponseQueryStatus = tagsResponseQuery.status;
  const tagsResponseQueryError = tagsResponseQuery.error;
  const tagsResponseQueryRefetch = tagsResponseQuery.refetch;
  const tagsQuery = useMemo<QueryResult<PublicTag[]>>(
    () => ({
      data: tagsResponseQueryData ? tags : null,
      status: tagsResponseQueryStatus,
      error: tagsResponseQueryError,
      refetch: tagsResponseQueryRefetch,
    }),
    [
      tags,
      tagsResponseQueryData,
      tagsResponseQueryStatus,
      tagsResponseQueryError,
      tagsResponseQueryRefetch,
    ],
  );

  const value = useMemo<TagsContextValue>(
    () => ({
      tagsQuery,
      tagsById: new Map(tags.map((tag) => [tag.id, tag])),
      tagsByName: new Map(tags.map((tag) => [tag.name, tag])),
    }),
    [tagsQuery, tags],
  );

  return <TagsContext.Provider value={value}>{children}</TagsContext.Provider>;
}

export function useTagsContext(): TagsContextValue {
  const ctx = useContext(TagsContext);
  if (!ctx) throw new Error("TagsProvider not mounted");
  return ctx;
}
